import store from '@/store/index'

export default {}

/**
 * Returns the full state object of the vehicle tracking module.
 *
 * @returns Object
 */
export const get = () => {
  return store.getters['assets/findVehiclesWidgetModule/get']
}

/**
 * Returns the sets of selected vehicles.
 *
 * @return Object
 */
export const selectedVehicleSets = () => {
  return store.getters['assets/findVehiclesWidgetModule/vehicleSelectionSets']
}

/**
 * Returns vehicle names that are selected for the specified set.
 *
 * @param set String
 *
 * @return Array
 */
export const selectedVehicleNames = (set) => {
  if (store.getters['assets/findVehiclesWidgetModule/vehicleSelectionSets'].hasOwnProperty(set)) {
    const vehicles = store.getters['assets/findVehiclesWidgetModule/vehicleSelectionSets'][set]
    let results = []

    for (let key in vehicles) {
      if (vehicles.hasOwnProperty(key)) {
        results.push(vehicles[key].reg_no)
      }
    }

    return results
  } else {
    return []
  }
}

/**
 * Returns vehicles that are selected for the specified set.
 *
 * @param set String
 *
 * @return Object
 */
export const selectedVehicles = (set) => {
  if (store.getters['assets/findVehiclesWidgetModule/vehicleSelectionSets'].hasOwnProperty(set)) {
    return store.getters['assets/findVehiclesWidgetModule/vehicleSelectionSets'][set]
  } else {
    return {}
  }
}

/**
 * Returns vehicles that are selected for the specified set as an array
 *
 * @param set String
 *
 * @return Array
 */
export const selectedVehiclesAsArray = (set) => {
  if (store.getters['assets/findVehiclesWidgetModule/vehicleSelectionSets'].hasOwnProperty(set)) {
    const vehicles = store.getters['assets/findVehiclesWidgetModule/vehicleSelectionSets'][set]
    const vehicleIds = Object.keys(vehicles)
    let results = []
    vehicleIds.forEach(vehicleId => {
      results.push(vehicles[vehicleId])
    })
    return results
  } else {
    return []
  }
}

/**
 * Returns an array of the selected vehicle's id's and their unit assignment
 * in the format:
 * [
 *   { id: 15, assigned_as: 'primary' },
 *   { id: 16, assigned_as: 'backup_1' },
 * ]
 *
 * @param set String
 *
 * @return {{assigned_as: *, id: *}[]}
 */
export const getSelectedVehIdsAndUnitAssigns = (set) => {
  const vehicles = selectedVehiclesAsArray(set)
  return vehicles.map((vehicle) => {
    return { id: vehicle.id, assigned_as: vehicle.unit_assigned_as }
  })
}

/**
 * Returns the first selected vehicle in the specified set or null if none is selected.
 *
 * @param set String
 *
 * @return Object|null
 */
export const selectedVehicle = (set) => {
  if (store.getters['assets/findVehiclesWidgetModule/vehicleSelectionSets'].hasOwnProperty(set)) {
    const vehicles = store.getters['assets/findVehiclesWidgetModule/vehicleSelectionSets'][set]
    const vehicleIds = Object.keys(vehicles)
    if (vehicleIds.length > 0) {
      return vehicles[vehicleIds[0]]
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * Returns true if the specified vehicle is selected within the supplied set.
 *
 * @param set String
 * @param id Integer
 *
 * @return Boolean
 */
export const isSelected = (set, id) => {
  return selectedVehicles(set).hasOwnProperty(id)
}

/**
 * Returns either a client object or null depending on whether
 * queries should be made by client.
 *
 * @return Object|null
 */
export const getClient = () => {
  return store.getters['assets/findVehiclesWidgetModule/getClient']
}

/**
 * Returns either an array of vehicle group objects or an empty array.
 *
 * @return Object|null
 */
export const getGroups = () => {
  return store.getters['assets/findVehiclesWidgetModule/getGroups']
}

/**
 * Load the status data of the vehicles being tracked.
 *
 * @return Promise
 */
export const query = () => {
  return store.dispatch('assets/findVehiclesWidgetModule/query')
}

/**
 * Load the vehicles assigned to a specific group.
 *
 * @param set
 * @param ids Array
 * @return Promise
 */
export const byGroupsWithUnits = (set, ids) => {
  return store.dispatch('assets/findVehiclesWidgetModule/byGroupsWithUnits', { set, ids })
}

/**
 * Add a map or array of object vehicles that have been selected within
 * the specified set.
 *
 * @param set String
 * @param vehicles Array|Map
 */
export const addSelectedVehicles = (set, vehicles) => {
  store.commit('assets/findVehiclesWidgetModule/addSelectedVehicles', { set, vehicles })
}

/**
 * Removes an array of vehicles from the set's list of selected vehicles.
 * The array must consist of the id's of the vehicles that are no
 * longer selected.
 *
 * @param set String
 * @param vehicles Array
 */
export const removeSelectedVehicles = (set, vehicles) => {
  store.commit('assets/findVehiclesWidgetModule/removeSelectedVehicles', { set, vehicles })
}

/**
 * Return all selected vehicles
 *
 * @param set
 * @returns {*}
 */
export const vehicleSelectionSets = (set) => {
  return store.getters['assets/findVehiclesWidgetModule/vehicleSelectionSets'][set]
}

/**
 * Clears all selected vehicles within a specified set.
 */
export const clearSelectedVehicles = (set) => {
  store.commit('assets/findVehiclesWidgetModule/clearSelectedVehicles', set)
}

/**
 * Clears all selected vehicles.
 */
export const clearAllSelectedVehicles = () => {
  store.commit('assets/findVehiclesWidgetModule/clearAllSelectedVehicles')
}

/**
 * Returns the selected vehicle count for the specified set.
 *
 * @param set String
 *
 * @return Integer
 */
export const selectedVehicleCount = (set) => {
  return Object.keys(selectedVehicles(set)).length
}

/**
 * Clears all vehicles from being tracked.
 */
export const purge = () => {
  store.commit('assets/findVehiclesWidgetModule/purge')
}

/**
 * Sets the client for which the vehicles should be queried.
 *
 * @param client Object|null
 */
export const setClient = (client) => {
  store.commit('assets/findVehiclesWidgetModule/setClient', client)
}

/**
 * Adds a vehicle group for which the vehicles should be queried.
 *
 * @param group Object|null
 */
export const setGroup = (group) => {
  store.commit('assets/findVehiclesWidgetModule/setGroup', group)
}

export const clearGroups = () => {
  store.commit('assets/findVehiclesWidgetModule/clearGroups')
}



// WEBPACK FOOTER //
// ./src/store/assets/vehicles/findVehiclesWidget.helpers.js